/**
 * Application principale : synchronisation de plusieurs vidéos YouTube (API IFrame).
 * Gère les URLs/décalages, la barre de contrôle (PLAY/STOP/RESET), le dimensionnement
 * responsive (dont mobile) et la boucle de correction de synchronisation.
 */
import React, { createContext, useEffect, useState } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import ReactGA from 'react-ga4';
import VideoPlayer from './VideoPlayer';
import './index.css';

ReactGA.initialize('G-WWZM144PJM');

/** Seuil de désync (secondes) au-delà duquel on corrige — volontairement large pour limiter les seeks. */
const SYNC_THRESHOLD_SEC = 0.4;
const SYNC_THRESHOLD_SEC_MOBILE = 0.8;
/** Intervalle de la boucle (ms) — pas trop court pour laisser les lecteurs avancer. */
const SYNC_INTERVAL_MS = 700;
const SYNC_INTERVAL_MS_MOBILE = 1000;
/** Délai (ms) après un seek avant de re-corriger ce lecteur. */
const SYNC_COOLDOWN_MS = 5000;
const SYNC_COOLDOWN_MS_MOBILE = 8000;
/** Pendant le warmup, aucune correction (seek) : on laisse les lecteurs se stabiliser après le démarrage. */
const SYNC_WARMUP_MS = 6000;
const SYNC_WARMUP_MS_MOBILE = 15000;

// Contexte global : état des lecteurs + registre pour la synchronisation
export const PlayerContext = createContext(undefined);

function App() {
    // Refs pour la synchronisation : horloge maître, registre des lecteurs, intervalle de correction
    const globalCurrentTimeRef = React.useRef(0);
    const syncStartTimestampRef = React.useRef(null);
    const registeredPlayersRef = React.useRef(new Map());
    const syncIntervalRef = React.useRef(null);

    // Vidéo gauche : URL, position de départ (s), mute
    const [videoLeft, setVideoLeft] = useState('');
    const [startLeft, setStartLeft] = useState(0);
    const [muteLeft, setMuteLeft] = useState(false);

    // Vidéo droite
    const [videoRight, setVideoRight] = useState('');
    const [startRight, setStartRight] = useState(0);
    const [muteRight, setMuteRight] = useState(false);

    // Vidéos additionnelles (liste d’objets { url, startSeconds, … })
    const [additionalVideos, setAdditionalVideos] = useState([]);
    const [videoNew, setVideoNew] = useState('');
    const [play, setPlay] = useState(false);
    const [quality, setQuality] = useState('large');

    // true si des vidéos ont été passées par l’URL (affichage du bouton TRY, etc.)
    const [videosParams, setVideosParams] = useState(false);

    // Accédez à l'état global (contexte) pour savoir quels lecteurs sont prêts
    const [playerReadyList, setPlayerReadyList] = useState([]);

    // Hauteur calculée des conteneurs vidéo (px), selon viewport et mobile/desktop
    const [videoHeight, setVideoHeight] = useState('85vh');

    const [playButtonDisabled, setPlayButtonDisabled] = useState(!videoLeft || !videoRight); // boutons PLAY / RESET désactivés si l'un des champs vidéo est vide

    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const [exportCopied, setExportCopied] = useState(false);

    /** Hex sans # depuis l’URL (bgColor) : fond des cartes et barre d’outils, pas seulement #AppId. */
    const [bgColorFromUrl, setBgColorFromUrl] = useState(null);
    /** HA=1 : pas de ligne URL/décalage (évite cases vides + bordures + « s » résiduels). */
    const [urlHideAllHeaders, setUrlHideAllHeaders] = useState(false);

    /** Construit l’URL complète avec les paramètres de la config actuelle (vidéos, décalages, mute). */
    const getExportUrl = React.useCallback(() => {
        const params = new URLSearchParams();
        if (videoLeft) {
            params.set('videoLeft', videoLeft);
            if (startLeft !== 0) params.set('startLeft', String(startLeft));
            if (muteLeft) params.set('muteLeft', '1');
        }
        if (videoRight) {
            params.set('videoRight', videoRight);
            if (startRight !== 0) params.set('startRight', String(startRight));
            if (muteRight) params.set('muteRight', '1');
        }
        additionalVideos.forEach((v, i) => {
            if (v.url) {
                params.set(`video${i + 1}`, v.url);
                if (v.startSeconds !== 0) params.set(`start${i + 1}`, String(v.startSeconds));
                if (v.mute) params.set(`mute${i + 1}`, '1');
            }
        });
        const query = params.toString();
        return query ? `${window.location.origin}${window.location.pathname}?${query}` : window.location.origin + window.location.pathname;
    }, [videoLeft, startLeft, muteLeft, videoRight, startRight, muteRight, additionalVideos]);

    /** Copie l’URL de config dans le presse-papier et affiche un retour visuel bref. */
    const handleExportConfig = React.useCallback(() => {
        const url = getExportUrl();
        navigator.clipboard.writeText(url).then(() => {
            setExportCopied(true);
            setTimeout(() => setExportCopied(false), 2000);
        });
    }, [getExportUrl]);

    /** Enregistre un lecteur pour la correction périodique de synchronisation. */
    const registerPlayer = React.useCallback((playerId, ref, startSeconds) => {
        registeredPlayersRef.current.set(playerId, { ref, startSeconds, lastSeekAt: 0 });
    }, []);

    /** Désenregistre un lecteur (nettoyage au démontage). */
    const unregisterPlayer = React.useCallback((playerId) => {
        registeredPlayersRef.current.delete(playerId);
    }, []);

    /** Boucle de synchronisation : horloge maître (performance.now) et correction des dérives. */
    useEffect(() => {
        if (!play) {
            if (syncIntervalRef.current) {
                clearInterval(syncIntervalRef.current);
                syncIntervalRef.current = null;
            }
            syncStartTimestampRef.current = null;
            return;
        }
        syncStartTimestampRef.current = performance.now();
        const isMobileDevice = isMobile && !isTablet;
        const thresholdSec = isMobileDevice ? SYNC_THRESHOLD_SEC_MOBILE : SYNC_THRESHOLD_SEC;
        const cooldownMs = isMobileDevice ? SYNC_COOLDOWN_MS_MOBILE : SYNC_COOLDOWN_MS;
        const warmupMs = isMobileDevice ? SYNC_WARMUP_MS_MOBILE : SYNC_WARMUP_MS;
        const intervalMs = isMobileDevice ? SYNC_INTERVAL_MS_MOBILE : SYNC_INTERVAL_MS;

        syncIntervalRef.current = setInterval(() => {
            const startTs = syncStartTimestampRef.current;
            if (!startTs) return;
            const now = performance.now();
            const elapsed = now - startTs;
            const timelineSec = elapsed / 1000;
            globalCurrentTimeRef.current = timelineSec * 1000;

            if (elapsed < warmupMs) return;

            for (const [, entry] of registeredPlayersRef.current.entries()) {
                const { ref, startSeconds, lastSeekAt } = entry;
                if (!ref || !ref.current) continue;
                if (lastSeekAt > 0 && now - lastSeekAt < cooldownMs) continue;
                const delay = startSeconds < 0 ? -startSeconds : 0;
                if (delay > 0 && timelineSec < delay) continue;
                const targetTime = timelineSec + startSeconds;
                try {
                    const current = ref.current.getCurrentTime();
                    if (typeof current !== 'number') continue;
                    if (Math.abs(current - targetTime) > thresholdSec) {
                        ref.current.seekTo(targetTime, true);
                        entry.lastSeekAt = now;
                    }
                } catch (_) { /* iframe peut être déchargé */ }
            }
        }, intervalMs);
        return () => {
            if (syncIntervalRef.current) {
                clearInterval(syncIntervalRef.current);
                syncIntervalRef.current = null;
            }
        };
    }, [play]);

    /** Passe la qualité en 'small' sur mobile (hors tablette) quand des paramètres vidéo sont présents. */
    function checkIfIsMobile(paramsForMobile) {
        if (paramsForMobile && isMobile && !isTablet) {
            setQuality('small');

            // @TODO: message if is iphone ?
            // document.getElementById('landscape-message').style.display = 'none';
            // document.getElementById('mobile-message').style.display = 'block';
        }
    }

    /** Affiche le message "paysage" si desktop en portrait sans vidéos additionnelles. */
    function checkOrientation(newVideosLength = 0) {
        if (!isMobile && newVideosLength === 0 && window.innerHeight > window.innerWidth) {
            document.getElementById('landscape-message').style.display = 'block';
        } else {
            document.getElementById('landscape-message').style.display = 'none';
        }
    }

    /** Réserve d'espace pour barre de contrôle + champs (header des cartes). */
    const MOBILE_RESERVE_PX = 220;
    const DESKTOP_RESERVE_PX = 80;

    /**
     * Calcule la hauteur des conteneurs vidéo pour couvrir au maximum le viewport.
     * Desktop (côte à côte) : une seule rangée → hauteur = viewport - réserve.
     * Mobile (empilé) : deux rangées → chaque vidéo = (viewport - réserve) / 2.
     */
    function updateVideoHeight() {
        const screenHeight = window.innerHeight;
        const isMobileDevice = isMobile && !isTablet;
        const reserve = isMobileDevice ? MOBILE_RESERVE_PX : DESKTOP_RESERVE_PX;
        const isRowLayout = window.matchMedia('(min-width: 768px)').matches;
        const calculatedHeight = isRowLayout
            ? (screenHeight - reserve)
            : Math.max(120, (screenHeight - reserve) / 2);
        setVideoHeight(`${Math.floor(calculatedHeight)}px`);
    }

    /** Applique la couleur de fond (hex sans #) à l’app et aux inputs/vidéos (paramètre URL bgColor). */
    function setBackgroundColor(backgroundColor) {
        document.getElementById('AppId').style.backgroundColor = '#' + backgroundColor;
        let inputs = document.getElementsByClassName('input');
        for (let i = 0; i < inputs.length; i++) {
            inputs[i].style.backgroundColor = '#' + backgroundColor;
        }
        let videos = document.getElementsByClassName('relative');
        for (let i = 0; i < videos.length; i++) {
            videos[i].style.border = '#' + backgroundColor;
            videos[i].style.backgroundColor = '#' + backgroundColor;
        }
        document.querySelectorAll('.tv, .bv').forEach(el => {
            el.style.backgroundColor = '#' + backgroundColor;
            el.style.borderRadius = 0;
        });
    }

    /** Masque le bouton ADD VIDEO (paramètre URL AV=0). */
    function hideAddVideoButton() {
        document.getElementById('addVideoButton').style.display = 'none';
    }

    /** Masque bouton ADD VIDEO, tous les inputs, labels et "second(s)" (paramètre URL HA=1). */
    function hideAll() {
        hideAddVideoButton();
        let elements = document.querySelectorAll('input, label ,.seconds');
        for (let i = 0; i < elements.length; i++) {
            elements[i].style.display = 'none';
        }
    }

    /** Met à jour l’URL de la vidéo left/right et l’état désactivé du bouton PLAY. */
    const setVideo = (value, player) => {
        if (player === 'left') {
            setVideoLeft(value);
        }
        if (player === 'right') {
            setVideoRight(value);
        }
        const nextLeft = player === 'left' ? value : videoLeft;
        const nextRight = player === 'right' ? value : videoRight;
        setPlayButtonDisabled(!nextLeft || !nextRight);
    };

    // Fonction pour ajouter un champ de vidéo supplémentaire
    const addVideoInput = () => {
        const newVideos = [...additionalVideos, {url: '', startSeconds: 0, mute: false}];
        setAdditionalVideos(newVideos);
        updateVideoHeight();
    };

    // Fonction pour mettre à jour les vidéos supplémentaires
    const updateAdditionalVideo = (index, value, field) => {
        const newVideos = [...additionalVideos];
        newVideos[index][field] = value;
        setAdditionalVideos(newVideos);
    };

    // Fonction pour ajouter une nouvelle vidéo
    const addVideo = () => {
        if (videoNew) {
            setAdditionalVideos([...additionalVideos, videoNew]);
            setVideoNew(''); // Réinitialiser le champ de saisie
        }
    };

    // Gérer le bouton de lecture/arrêt
    const handlePlayStop = () => {
        setPlay(!play);
    };

    /** Avance ou recule toutes les vidéos de deltaSec secondes de manière synchronisée (met à jour l’horloge maître puis seek chaque lecteur). */
    const seekAllBy = React.useCallback((deltaSec) => {
        const now = performance.now();
        const startTs = syncStartTimestampRef.current;
        if (!startTs) return;
        const elapsedMs = now - startTs;
        let newElapsedMs = elapsedMs + deltaSec * 1000;
        if (newElapsedMs < 0) newElapsedMs = 0;
        syncStartTimestampRef.current = now - newElapsedMs;
        const newTimelineSec = newElapsedMs / 1000;
        globalCurrentTimeRef.current = newElapsedMs;

        for (const [, entry] of registeredPlayersRef.current.entries()) {
            const { ref, startSeconds } = entry;
            if (!ref?.current) continue;
            const delay = startSeconds < 0 ? -startSeconds : 0;
            if (delay > 0 && newTimelineSec < delay) continue;
            const targetTime = newTimelineSec + startSeconds;
            if (targetTime < 0) continue;
            try {
                ref.current.seekTo(targetTime, true);
                entry.lastSeekAt = now;
            } catch (_) { /* iframe peut être déchargé */ }
        }
    }, []);

    const SEEK_STEP_SEC = 30;
    const handleSeekBack = () => seekAllBy(-SEEK_STEP_SEC);
    const handleSeekForward = () => seekAllBy(SEEK_STEP_SEC);

    // Masque les loaders (.nh) quand les deux lecteurs principaux sont prêts
    useEffect(() => {
        if (playerReadyList.includes('left') && playerReadyList.includes('right')) {
            document.querySelectorAll('.nh').forEach(el => el.style.opacity = '0');
        }
    }, [playerReadyList]);

    // Fonction pour réinitialiser les vidéos
    const handleReset = () => {
        setPlayButtonDisabled(true);
        document.querySelectorAll('.nh').forEach(el => {
            el.style.transition = 'opacity 2s ease-out';
            el.style.opacity = '1';
        });

        setPlay(false); // Arrêter les vidéos

        // Réinitialiser les vidéos avec les valeurs actuelles de décalage
        if (videoLeft && videoRight) {
            setVideoLeft('');
            setVideoRight('');
            setTimeout(function () {
                setVideoLeft(videoLeft);
                setVideoRight(videoRight);
            }, 100);
            setTimeout(() => setVideoLeft(videoLeft), 100);
        }

        setAdditionalVideos(additionalVideos.map(video => ({...video, url: ''})));
        setTimeout(() => setAdditionalVideos(additionalVideos), 100);

        setTimeout(function () {
            document.querySelectorAll('.nh').forEach(el => el.style.opacity = '0');
            setTimeout(() => document.querySelectorAll('.nh').forEach(el => el.style.transition = 'opacity 0s'), 2000);
            setPlayButtonDisabled(false);
        }, 2000);

        document.querySelectorAll('.tv, .bv').forEach(el => {
            el.style.opacity = '1';
        });
    };

    /** Au premier chargement : reset si des vidéos viennent de l’URL, sinon masquage progressif des loaders. */
    function init(videosParams) {
        if (videosParams === true) {
            // Initial reset
            handleReset();
        } else {
            // document.querySelectorAll('.nh').forEach(el => el.style.opacity = '1');
            document.querySelectorAll('.nh').forEach(el => {
                // el.style.transition = 'opacity 3s ease-out';
                el.style.opacity = '0';
            });
            setTimeout(() => document.querySelectorAll('.nh').forEach(el => el.style.transition = 'opacity 2s ease-out'), 5000);
        }
    }

    // Montage : calcul hauteur vidéos, listeners resize/orientation, lecture des paramètres URL
    useEffect(() => {
        updateVideoHeight();
        const onResize = () => {
            checkOrientation();
            updateVideoHeight();
        };
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);

        // Récupérez les paramètres de recherche de l'URL de la fenêtre
        const queryParams = new URLSearchParams(window.location.search);

        // videoLeft
        const videoLeftParam = queryParams.get('videoLeft');
        const startLeftParam = Number(queryParams.get('startLeft'));
        const muteLeftParam = queryParams.get('muteLeft');
        if (videoLeftParam) setVideoLeft(videoLeftParam);
        if (startLeftParam) setStartLeft(startLeftParam);
        if (muteLeftParam) setMuteLeft(muteLeftParam);

        // videoRight
        const videoRightParam = queryParams.get('videoRight');
        const startRightParam = Number(queryParams.get('startRight'));
        const muteRightParam = queryParams.get('muteRight');
        if (videoRightParam) setVideoRight(videoRightParam);
        if (startRightParam) setStartRight(startRightParam);
        if (muteRightParam) setMuteRight(muteRightParam);

        if (videoLeftParam || videoRightParam) {
            setVideosParams(true);
        }

        const backgroundColorParam = queryParams.get('bgColor');
        if (backgroundColorParam) {
            setBgColorFromUrl(backgroundColorParam);
            setBackgroundColor(backgroundColorParam);
        }

        const addVideoParam = queryParams.get('AV');
        if (addVideoParam && addVideoParam === '0') hideAddVideoButton();

        const hideAllParam = queryParams.get('HA');
        if (hideAllParam && hideAllParam === '1') {
            setUrlHideAllHeaders(true);
            hideAll();
        }

        // Gérer les vidéos additionnelles
        const newVideos = [];
        for (let i = 1; queryParams.get(`video${i}`); i++) {
            newVideos.push({
                url: queryParams.get(`video${i}`),
                startSeconds: Number(queryParams.get(`start${i}`) || 0),
                mute: queryParams.get(`mute${i}`),
                bgColor: '#' + backgroundColorParam || '#FFF',
                display: (hideAllParam && hideAllParam === '1') ? 'none' : 'block'
            });
        }
        if (newVideos.length > 0) {
            setAdditionalVideos(newVideos);
            updateVideoHeight();
            document.getElementById('landscape-message').style.display = 'none';
        }

        // Initial mobile checking
        checkIfIsMobile(backgroundColorParam && videoLeftParam && videoRightParam);

        // Initial orientation checking for mobile
        checkOrientation(newVideos.length);
        
        init(videoLeftParam !== null && videoRightParam !== null);
    }, []);

    const urlBgCss = bgColorFromUrl ? {backgroundColor: `#${bgColorFromUrl}`} : {};
    const noVideoChrome = urlHideAllHeaders;
    const urlCardClass = noVideoChrome
        ? 'border-0 shadow-none'
        : bgColorFromUrl
            ? 'border border-neutral-600'
            : 'border border-neutral-200 bg-white';
    /** Colonnes vidéo sans bordure ni ombre quand HA=1 (souvent avec fond noir). */
    const videoColumnStyle = noVideoChrome
        ? {...(bgColorFromUrl ? {backgroundColor: `#${bgColorFromUrl}`} : {}), border: 'none', boxShadow: 'none'}
        : urlBgCss;
    const urlHeaderRowBase = bgColorFromUrl
        ? 'flex flex-nowrap items-center gap-1.5 md:gap-2 py-2 md:py-3 px-4 md:px-6 lg:px-8 border-b border-neutral-600 min-w-0'
        : 'flex flex-nowrap items-center gap-1.5 md:gap-2 py-2 md:py-3 px-4 md:px-6 lg:px-8 border-b border-neutral-400 min-w-0 bg-gray-200';
    const urlHeaderRowClass = urlHeaderRowBase;

    return (
        <PlayerContext.Provider value={{
            playerReadyList,
            setPlayerReadyList,
            globalCurrentTimeRef,
            syncStartTimestampRef,
            registerPlayer,
            unregisterPlayer,
        }}>
            <div className={`App ${bgColorFromUrl ? '' : 'bg-neutral-50'}`} id="AppId" style={bgColorFromUrl ? {backgroundColor: `#${bgColorFromUrl}`} : undefined}>
                <div id="mobile-message"
                     className="hidden fixed left-0 right-0 w-auto h-full bg-gray-900 text-white text-center whitespace-normal text-2xl p-10 opacity-80 overflow-scroll">
                    <div className="py-6"><p>🇫🇷</p>C'est chouette le smartphone, mais ce site mérite un écran plus large
                        pour être pleinement apprécié... rendez-vous sur votre ordinateur ?
                    </div>
                    <p className="py-10">💻 🤩</p>
                    <div className="py-6"><p>🇬🇧</p>Smartphones are great, but this website deserves a larger screen to
                        be
                        fully appreciated... so why don't you go to your computer?
                    </div>
                </div>

                <div id="landscape-message"
                     className="hidden fixed left-0 right-0 w-auto h-full bg-gray-900 text-white text-center whitespace-normal text-2xl p-10 opacity-80 overflow-scroll">
                    <div className="py-8"><p>🇫🇷</p>Je vous assure qu'on en profite mieux en mode paysage, tournez votre
                        écran s'il vous plaît !
                    </div>
                    <p className="py-12">🎬 🤩</p>
                    <div className="py-8"><p>🇬🇧</p>I assure you it's best viewed in landscape mode, so please turn your
                        screen around!
                    </div>
                </div>

                <a href={"https://streettrumpet.info/videos"} title={"Go go go streettrumpet.info"} target={"_blank"}>
                    <div
                        className="absolute top-0 right-0 h-75 p-2 px-3 bg-gray-700 rounded-bl-xl text-xs text-white opacity-20 hover:bg-gray-900">#STREETTRUMPET
                    </div>
                </a>

                <div id="infobulle" className="absolute top-0 left-0 h-75 p-2 px-3">
                    <div className="group" onClick={() => setIsTooltipOpen(!isTooltipOpen)}>
                        <div
                            className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-700 text-white cursor-pointer opacity-20 hover:bg-gray-900">
                            <span>?</span>
                        </div>
                        <div className={`${isTooltipOpen ? "block" : "hidden"} absolute top-2 left-10 mb-6 w-96`}>
                            <div className="text-sm text-justify text-white p-2 bg-gray-400 rounded">
                                <p className="p-1.5"><span className="pr-2">🇫🇷</span>Le concept : pouvoir synchroniser
                                    deux vidéos (ou plus) indépendantes et visibles
                                    en ligne sur Youtube — grâce à son API pardi !</p>
                                <p className="pl-10 text-blue-900 hover:text-blue-600"><a
                                    href={"https://play.streettrumpet.info/?videoLeft=pwnefUaKCbc&startLeft=15.8&videoRight=LY2tjBFK20c&startRight=0&muteRight=1"} title={"Enjoy !"} target={"_blank"}>Juste
                                    un exemple de ce qu'on peut faire 🤩<br/>Just one example of what we can do 🤩</a></p>
                                <p className="p-1.5"><span className="pr-2">🇬🇧</span>The concept: to synchronize two (or
                                    more) independent videos that can be viewed
                                    online on Youtube - thanks to its API, of course!</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col items-center w-full h-screen min-h-0 overflow-hidden">
                    {/* Barre de contrôle : compacte pour laisser un max de place aux vidéos */}
                    <div className="w-full max-w-5xl mx-auto px-3 py-2 md:px-5 md:py-3 flex-shrink-0">
                        <div
                            className={`flex flex-wrap items-center justify-center gap-3 md:gap-4 py-3 px-4 rounded-xl ${
                                noVideoChrome && bgColorFromUrl
                                    ? 'border-0 shadow-none'
                                    : `border shadow-sm ${bgColorFromUrl ? 'border-neutral-600' : 'border-neutral-200 bg-white'}`
                            }`}
                            style={
                                noVideoChrome && bgColorFromUrl
                                    ? {backgroundColor: `#${bgColorFromUrl}`, border: 'none', boxShadow: 'none'}
                                    : urlBgCss
                            }
                        >
                            {/* Groupe Lecture : PLAY, avance/recule, RESET */}
                            <div className="flex items-center gap-2 flex-wrap justify-center">
                                <button
                                    id="playButton"
                                    onClick={handlePlayStop}
                                    disabled={playButtonDisabled}
                                    className={`text-white text-base font-bold py-2 px-5 rounded-lg transition-colors duration-200 ${playButtonDisabled ? 'bg-neutral-400 cursor-not-allowed' : 'bg-green-700 hover:bg-green-600'}`}
                                >
                                    {play ? 'STOP' : 'PLAY'}
                                </button>
                                <button
                                    onClick={handleSeekBack}
                                    disabled={!play}
                                    title="-30 s"
                                    className={`text-white text-base font-bold py-2 px-4 rounded-lg transition-colors duration-200 ${!play ? 'bg-neutral-400 cursor-not-allowed' : 'bg-amber-600 hover:bg-amber-500'}`}
                                >
                                    −30 s
                                </button>
                                <button
                                    onClick={handleSeekForward}
                                    disabled={!play}
                                    title="+30 s"
                                    className={`text-white text-base font-bold py-2 px-4 rounded-lg transition-colors duration-200 ${!play ? 'bg-neutral-400 cursor-not-allowed' : 'bg-amber-600 hover:bg-amber-500'}`}
                                >
                                    +30 s
                                </button>
                                <button
                                    onClick={handleReset}
                                    disabled={playButtonDisabled}
                                    className={`text-white text-base font-bold py-2 px-5 rounded-lg transition-colors duration-200 ${playButtonDisabled ? 'bg-neutral-400 cursor-not-allowed' : 'bg-green-700 hover:bg-green-600'}`}
                                >
                                    RESET
                                </button>
                            </div>
                            {/* Séparateur visuel */}
                            <div className="hidden sm:block w-px h-8 bg-neutral-200" aria-hidden="true" />
                            {/* Groupe Configuration : ADD VIDEO, recharger, TRY */}
                            <div className="flex items-center gap-2 flex-wrap justify-center">
                                <button
                                    id="addVideoButton"
                                    onClick={addVideoInput}
                                    className="text-white text-base font-bold py-2 px-5 rounded-lg transition-colors duration-200 bg-red-700 hover:bg-red-600"
                                >
                                    ADD VIDEO
                                </button>
                                <button
                                    onClick={() => window.location.reload()}
                                    title="Recharger la page"
                                    className="text-white text-base font-bold p-2 rounded-lg transition-colors duration-200 bg-neutral-600 hover:bg-neutral-500"
                                >
                                    <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 22" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd"
                                              d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                                              clipRule="evenodd"/>
                                    </svg>
                                </button>
                                <button
                                    onClick={handleExportConfig}
                                    title="Exporter la config (copier l’URL avec paramètres)"
                                    className={`text-white text-base font-bold p-2 rounded-lg transition-colors duration-200 ${exportCopied ? 'bg-green-600' : 'bg-neutral-600 hover:bg-neutral-500'}`}
                                >
                                    <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path d="M12.232 4.232a2.5 2.5 0 013.536 3.536l-1.225 1.224a.75.75 0 001.061 1.06l1.224-1.224a4 4 0 00-5.656-5.656l-3 3a4 4 0 00.225 5.865.75.75 0 00.977-1.138 2.5 2.5 0 01-.14-3.667l3-3z"/>
                                        <path d="M11.603 7.963a.75.75 0 00-.977 1.138 2.5 2.5 0 01.14 3.667l-3 3a2.5 2.5 0 01-3.536-3.536l1.225-1.224a.75.75 0 00-1.061-1.06l-1.224 1.224a4 4 0 105.656 5.656l3-3a4 4 0 00-.225-5.865z"/>
                                    </svg>
                                </button>
                                {videosParams && (
                                    <button
                                        id="tryButton"
                                        onClick={() => window.open('https://play.streettrumpet.info/', '_blank')}
                                        className="text-white text-base font-bold py-2 px-5 rounded-lg transition-colors duration-200 bg-blue-700 hover:bg-blue-600"
                                    >
                                        TRY
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    {exportCopied && (
                        <div
                            className="fixed bottom-6 left-1/2 z-[10000] -translate-x-1/2 rounded-lg bg-gray-900 px-4 py-2.5 text-center text-sm text-white shadow-lg ring-1 ring-white/10"
                            role="status"
                            aria-live="polite"
                        >
                            <span className="font-medium">Lien copié dans le presse-papier</span>
                            <span className="mt-0.5 block text-xs text-neutral-300">Link copied to clipboard</span>
                        </div>
                    )}

                    {/* Zone vidéos : rangée principale en flex-1, additionnelles en dessous */}
                    <div className="flex flex-col flex-1 min-h-0 w-full overflow-auto">
                    {/* Rangée des 2 vidéos principales : occupe toute la hauteur restante */}
                    <div className="flex flex-col w-full flex-1 min-h-0 gap-4 md:flex-row md:gap-0">

                        {/* Bloc left : champs sur une ligne + vidéo */}
                        <div className={`flex-1 flex flex-col rounded-xl md:rounded-r-none overflow-hidden min-w-0 ${noVideoChrome ? '' : 'md:border-r-1 md:border-r-neutral-300'} ${urlCardClass}`} style={videoColumnStyle}>
                            {!urlHideAllHeaders && (
                            <div className={urlHeaderRowClass} style={bgColorFromUrl ? urlBgCss : undefined}>
                                <label htmlFor="videoLeft" className="text-gray-500 text-xs md:text-sm shrink-0 whitespace-nowrap">Youtube URL / ID</label>
                                <input
                                    id="videoLeft"
                                    type="text"
                                    placeholder="coller ici le lien complet ou l'ID de la vidéo / paste the full link or video ID here"
                                    value={videoLeft}
                                    onChange={(e) => setVideo(e.target.value, 'left')}
                                    className="input input-bordered min-w-[5rem] flex-1 p-1.5 text-xs md:text-sm border border-neutral-200"
                                />
                                <label htmlFor="startLeft" className="text-gray-500 text-xs md:text-sm shrink-0">starts at</label>
                                <input
                                    id="startLeft"
                                    type="number"
                                    step="0.01"
                                    value={startLeft}
                                    onChange={(e) => setStartLeft(e.target.value)}
                                    className="input input-bordered w-14 md:w-20 p-1.5 md:p-2 text-xs md:text-sm shrink-0 border border-neutral-200"
                                />
                                <span className="seconds text-gray-500 text-xs md:text-sm shrink-0">s</span>
                                <label className="ml-3 flex items-center gap-1 pl-2 text-gray-500 text-xs md:text-sm cursor-pointer shrink-0 md:ml-4 md:pl-3">
                                    <input
                                        type="checkbox"
                                        checked={muteLeft}
                                        onChange={(e) => setMuteLeft(e.target.checked)}
                                        className="checkbox checkbox-sm"
                                    />
                                    <span>mute</span>
                                </label>
                            </div>
                            )}
                            <div
                                className={`relative w-full bg-black flex-1 min-h-0 ${urlHideAllHeaders ? 'rounded-xl md:rounded-r-none' : 'rounded-b-xl md:rounded-br-none'}`}>
                                    <div className="tv bg-black rounded-t-lg"></div>
                                    {!videoLeft && (
                                        <div
                                            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                            <div className="text-gray-500">
                                                <p className="py-6 text-center text-6xl">🎞️</p>
                                                <p className="py-2"><span className="px-2">🇫🇷</span>Votre vidéo #left
                                                    apparaîtra ici</p>
                                                <p className="py-2"><span className="px-2">🇬🇧</span>Your #left video
                                                    will appear here</p>
                                            </div>
                                        </div>
                                    )}
                                    {videoLeft && (
                                        <VideoPlayer url={videoLeft} startSeconds={startLeft} play={play}
                                                     mute={muteLeft} player={'left'} quality={quality}/>
                                    )}
                                    <div className="bv bg-black rounded-b-lg"></div>
                                    <div className="nh bg-black"><span className="loader"></span></div>
                                </div>
                        </div>

                        {/* Bloc right : champs sur une ligne + vidéo, collé à gauche */}
                        <div className={`flex-1 flex flex-col rounded-xl md:rounded-l-none overflow-hidden min-w-0 ${urlCardClass}`} style={videoColumnStyle}>
                            {!urlHideAllHeaders && (
                            <div className={urlHeaderRowClass} style={bgColorFromUrl ? urlBgCss : undefined}>
                                <label htmlFor="videoRight" className="text-gray-500 text-xs md:text-sm shrink-0 whitespace-nowrap">Youtube URL / ID</label>
                                <input
                                    id="videoRight"
                                    type="text"
                                    placeholder="coller ici le lien complet ou l'ID de la vidéo / paste the full link or video ID here"
                                    value={videoRight}
                                    onChange={(e) => setVideo(e.target.value, 'right')}
                                    className="input input-bordered min-w-[5rem] flex-1 p-1.5 text-xs md:text-sm border border-neutral-200"
                                />
                                <label htmlFor="startRight" className="text-gray-500 text-xs md:text-sm shrink-0">starts at</label>
                                <input
                                    id="startRight"
                                    type="number"
                                    step="0.01"
                                    value={startRight}
                                    onChange={(e) => setStartRight(e.target.value)}
                                    className="input input-bordered w-14 md:w-20 p-1.5 md:p-2 text-xs md:text-sm shrink-0 border border-neutral-200"
                                />
                                <span className="seconds text-gray-500 text-xs md:text-sm shrink-0">s</span>
                                <label className="ml-3 flex items-center gap-1 pl-2 text-gray-500 text-xs md:text-sm cursor-pointer shrink-0 md:ml-4 md:pl-3">
                                    <input
                                        type="checkbox"
                                        checked={muteRight}
                                        onChange={(e) => setMuteRight(e.target.checked)}
                                        className="checkbox checkbox-sm"
                                    />
                                    <span>mute</span>
                                </label>
                            </div>
                            )}
                            <div
                                className={`relative w-full bg-black flex-1 min-h-0 ${urlHideAllHeaders ? 'rounded-xl md:rounded-l-none' : 'rounded-b-xl md:rounded-bl-none'}`}>
                                    <div className="tv bg-black rounded-t-lg"></div>
                                    {!videoRight && (
                                        <div
                                            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                            <div className="text-gray-500">
                                                <p className="py-6 text-center text-6xl">🎞️</p>
                                                <p className="py-2"><span className="px-2">🇫🇷</span>Votre vidéo #right
                                                    apparaîtra ici</p>
                                                <p className="py-2"><span className="px-2">🇬🇧</span>Your #right video
                                                    will appear here</p>
                                            </div>
                                        </div>
                                    )}
                                    {videoRight && (
                                        <VideoPlayer url={videoRight} startSeconds={startRight} play={play}
                                                     mute={muteRight} player={'right'} quality={quality}/>
                                    )}
                                    <div className="bv bg-black rounded-b-lg"></div>
                                    <div className="nh bg-black"><span className="loader"></span></div>
                                </div>
                        </div>
                    </div>

                    <div className="flex w-full mt-2 flex-col gap-4 md:flex-row md:flex-wrap flex-shrink-0">
                        {/* Blocs vidéos supplémentaires : champs + vidéo regroupés */}
                        {additionalVideos.map((video, index) => (
                            <div key={`video-${index}`} className={`flex-1 flex flex-col rounded-xl overflow-hidden min-w-0 min-h-0 ${urlCardClass}`} style={{display: video.display, borderLeftWidth: urlHideAllHeaders ? 0 : '4px', borderLeftColor: video.bgColor || 'transparent', ...videoColumnStyle}}>
                                {!urlHideAllHeaders && (
                                <div className={urlHeaderRowClass} style={bgColorFromUrl ? urlBgCss : undefined}>
                                    <label htmlFor={"urlNew" + index} className="text-gray-500 text-xs md:text-sm shrink-0 whitespace-nowrap">Youtube URL / ID</label>
                                    <input
                                        id={"urlNew" + index}
                                        type="text"
                                        placeholder="coller ici le lien complet ou l'ID de la vidéo / paste the full link or video ID here"
                                        value={video.url}
                                        onChange={(e) => updateAdditionalVideo(index, e.target.value, 'url')}
                                        className="input input-bordered min-w-[5rem] flex-1 p-1.5 text-xs md:text-sm border border-neutral-200"
                                    />
                                    <label htmlFor={"startNew" + index} className="text-gray-500 text-xs md:text-sm shrink-0">starts at</label>
                                    <input
                                        id={"startNew" + index}
                                        type="number"
                                        step="0.01"
                                        value={video.startSeconds}
                                        onChange={(e) => updateAdditionalVideo(index, e.target.value, 'startSeconds')}
                                        className="input input-bordered w-14 md:w-20 p-1.5 md:p-2 text-xs md:text-sm shrink-0 border border-neutral-200"
                                    />
                                    <span className="seconds text-gray-500 text-xs md:text-sm shrink-0">s</span>
                                    <label className="ml-3 flex items-center gap-1 pl-2 text-gray-500 text-xs md:text-sm cursor-pointer shrink-0 md:ml-4 md:pl-3">
                                        <input
                                            type="checkbox"
                                            checked={!!video.mute}
                                            onChange={(e) => updateAdditionalVideo(index, e.target.checked, 'mute')}
                                            className="checkbox checkbox-sm"
                                        />
                                        <span>mute</span>
                                    </label>
                                </div>
                                )}
                                <div
                                    className={`relative w-full bg-black flex-1 min-h-[200px] ${urlHideAllHeaders ? 'rounded-xl' : 'rounded-b-xl'}`}
                                    style={{height: videoHeight}}>
                                        <div className="tv bg-black rounded-t-lg"></div>
                                        {!video.url && (
                                            <div
                                                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                                <div className="text-gray-500">
                                                    <p className="py-6 text-center text-6xl">🎞️</p>
                                                    <p className="py-2"><span className="px-2">🇫🇷</span>Votre vidéo
                                                        apparaîtra ici</p>
                                                    <p className="py-2"><span className="px-2">🇬🇧</span>Your video will
                                                        appear here</p>
                                                </div>
                                            </div>
                                        )}
                                        {video.url && (
                                            <VideoPlayer url={video.url} startSeconds={video.startSeconds} play={play}
                                                         mute={video.mute} player={index} quality={quality}/>
                                        )}
                                        <div className="bv bg-black rounded-b-lg"></div>
                                        <div className="nh"></div>
                                    </div>
                            </div>
                        ))}
                    </div>
                    </div>
                </div>
            </div>
        </PlayerContext.Provider>
    );

}

export default App;